/**
 * Cart Max Quantity Guard
 *
 * cart-template.liquid sayfasindaki +/- butonlarina max_qty_ kontrolu ekler.
 * Urunun tag'lerinde max_qty_X varsa, toplam adet X'i asamaz.
 * Asilmaya calisilirsa toast uyarisi gosterir.
 *
 * Sayfa yuklendiginde mevcut sepetteki miktarlari da kontrol eder,
 * limiti asanlari otomatik olarak duzeltir.
 *
 * cart.js icerisindeki max_qty_ mantigi ile birebir ayni calismaktadir.
 */
import { showToast } from '@nutima-themes/utils/toast';

(async function CartMaxQtyGuard() {
  const maxQtyMap = {};
  let cartItems = [];

  try {
    const cartRes = await fetch('/cart.js');
    const cartData = await cartRes.json();
    cartItems = cartData.items;
  } catch (e) {
    console.error('[CartMaxQty] Sepet verisi alinamadi:', e);
    return;
  }

  const lineItems = document.querySelectorAll('tr.line-item[data-product-handle]');
  const handles = new Set();

  lineItems.forEach(function (item) {
    if (item.dataset.productHandle) {
      handles.add(item.dataset.productHandle);
    }
  });

  const fetchPromises = [];
  handles.forEach(function (handle) {
    fetchPromises.push(
      fetch('/products/' + handle + '.json')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.product.tags.includes('max_qty_')) {
            var tagList = data.product.tags.split(', ');
            var maxTag = tagList.find(function (t) { return t.includes('max_qty_'); });
            if (maxTag) {
              var maxQty = parseInt(maxTag.replace('max_qty_', ''));
              if (!isNaN(maxQty)) {
                maxQtyMap[data.product.id] = maxQty;
              }
            }
          }
        })
        .catch(function (e) {
          console.error('[CartMaxQty] Urun verisi alinamadi: ' + handle, e);
        })
    );
  });

  await Promise.all(fetchPromises);

  if (Object.keys(maxQtyMap).length === 0) return;

  /**
   * SAYFA YUKLENDIGINDE: Limiti asan urunleri otomatik duzelt
   * cart.js'deki ayni mantik: her urunun sepetteki toplam adedini kontrol et,
   * max_qty_ degerini asiyorsa miktarlari duzelt ve toast goster.
   */
  var needsCorrection = false;
  var allUpdates = {};

  var checkedProductIds = {};
  for (var i = 0; i < cartItems.length; i++) {
    var item = cartItems[i];
    var pid = item.product_id;

    if (checkedProductIds[pid]) continue;
    checkedProductIds[pid] = true;

    var maxQtyForProduct = maxQtyMap[pid];
    if (!maxQtyForProduct) continue;

    var sameProductItems = cartItems.filter(function (ci) {
      return ci.product_id === pid;
    });

    var totalQty = 0;
    sameProductItems.forEach(function (ci) { totalQty += ci.quantity; });

    if (totalQty > maxQtyForProduct) {
      needsCorrection = true;
      var remaining = maxQtyForProduct;

      sameProductItems.forEach(function (ci) {
        remaining -= ci.quantity;
        if (remaining < 0) {
          allUpdates[ci.id] = remaining + ci.quantity;
        } else {
          allUpdates[ci.id] = ci.quantity;
        }
      });

      showToast({
        message: 'Bu üründen sepete en fazla ' + maxQtyForProduct + ' adet ekleyebilirsiniz.',
        type: 'warning'
      });
    }
  }

  if (needsCorrection) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: allUpdates })
      });
      location.reload();
      return;
    } catch (e) {
      console.error('[CartMaxQty] Sepet duzeltme basarisiz:', e);
    }
  }

  /**
   * BUTON DINLEYICILERI: Artirma/azaltma butonlarini yakala
   */
  function getTotalQtyForProduct(productId) {
    var total = 0;
    for (var j = 0; j < cartItems.length; j++) {
      if (cartItems[j].product_id === productId) {
        total += cartItems[j].quantity;
      }
    }
    return total;
  }

  function refreshCartCache() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (data) { cartItems = data.items; })
      .catch(function () {});
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-action="increase-quantity"]');
    if (!button) return;

    var lineItem = button.closest('tr.line-item[data-product-id]');
    if (!lineItem) return;

    var productId = parseInt(lineItem.dataset.productId);
    var maxQty = maxQtyMap[productId];
    if (!maxQty) return;

    var currentTotal = getTotalQtyForProduct(productId);

    if (currentTotal >= maxQty) {
      event.preventDefault();
      event.stopImmediatePropagation();

      showToast({
        message: 'Bu üründen sepete en fazla ' + maxQty + ' adet ekleyebilirsiniz.',
        type: 'warning'
      });
      return;
    }

    var line = parseInt(button.dataset.line);
    if (line > 0 && line <= cartItems.length) {
      cartItems[line - 1].quantity += 1;
    }
  }, true);

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-action="decrease-quantity"]');
    if (!button) return;

    var lineItem = button.closest('tr.line-item[data-product-id]');
    if (!lineItem) return;

    var newQty = parseInt(button.dataset.quantity);
    var line = parseInt(button.dataset.line);

    if (line > 0 && line <= cartItems.length) {
      cartItems[line - 1].quantity = Math.max(0, newQty);
    }

    setTimeout(refreshCartCache, 2000);
  }, true);

  document.querySelectorAll('.quantity-selector__value').forEach(function (input) {
    input.addEventListener('change', function (event) {
      var lineItem = input.closest('tr.line-item[data-product-id]');
      if (!lineItem) return;

      var productId = parseInt(lineItem.dataset.productId);
      var maxQty = maxQtyMap[productId];
      if (!maxQty) return;

      var newValue = parseInt(input.value);
      var currentValue = parseInt(input.dataset.currentValue);

      if (isNaN(newValue) || newValue < 0) {
        input.value = currentValue;
        return;
      }

      var currentTotal = getTotalQtyForProduct(productId);
      var delta = newValue - currentValue;
      var newTotal = currentTotal + delta;

      if (newTotal > maxQty) {
        event.preventDefault();
        event.stopImmediatePropagation();

        var maxAllowed = currentValue + (maxQty - currentTotal);
        input.value = Math.max(0, maxAllowed);

        showToast({
          message: 'Bu üründen sepete en fazla ' + maxQty + ' adet ekleyebilirsiniz.',
          type: 'warning'
        });
        return;
      }

      var line = parseInt(input.dataset.line);
      if (line > 0 && line <= cartItems.length) {
        cartItems[line - 1].quantity = newValue;
      }
    }, true);
  });
})();
